/*
The card that represents a draw from the past.
Contains the date, the jackpot value, the winning
key and some other statistics. Also contains a
withdraw button that allows the user to claim
the prize into their wallet (if applicable).
*/

import { Address } from "viem";
import { Draw } from "../../models/Draw";
import { CURRENCY } from "../../utils/Constants";
import { formatDate } from "../../utils/Utils";
import TicketEntry from "./TicketEntry";

export interface DrawCardProps {
  draw: Draw;
  onClick: () => void;
  address?: Address;
}

export default function DrawCard(props: DrawCardProps) {
  let hasBorder: boolean = false;
  let borderStyle: string = "";

  /**
   * Choose correct outline color, following the
   * line of priority below.
   */
  const played: boolean =
    props.address != undefined && props.draw.ticketsBought.length > 0;
  const benefited: boolean =
    props.address != undefined &&
    props.address.toLowerCase() == props.draw.charity?.address?.toLowerCase();

  if (played && benefited) {
    hasBorder = true;
    borderStyle =
      props.draw.userPrize!.value > 0n
        ? "bg-gradient-to-b from-mango to-bermuda"
        : "bg-gradient-to-b from-emperor to-bermuda";
  } else if (played) {
    hasBorder = true;
    borderStyle = props.draw.userPrize!.value > 0n ? "bg-mango" : "bg-emperor";
  } else if (benefited) {
    hasBorder = true;
    borderStyle = "bg-bermuda";
  }

  return (
    <div className="flex flex-col items-center justify-center">
      <div
        onClick={props.onClick}
        className={`${hasBorder && borderStyle} w-fit shadow-xl cursor-pointer rounded-lg hover:scale-105 duration-250`}
      >
        <div
          className={`${hasBorder && "m-[2px]"} select-none flex flex-col justify-start items-center rounded-lg bg-bastille ~p-1.5/2.5`}
        >
          <p className="font-archb ~text-xs/sm bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
            #{props.draw.number.toString()}
          </p>
          <span className="font-poppins font-bold ~text-xs/sm text-gray">
            {formatDate(props.draw.date, false)}
          </span>
          <div className="flex flex-row ~mb-1/2 ~mt-0.5/1.5">
            <p className="font-archb font-bold ~text-xl/xxxl ~leading-6/9 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
              {props.draw.jackpot.pretty}
            </p>
            <p className="font-archb font-bold ~text-xl/xxxl ~leading-6/9 bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
              &nbsp;{CURRENCY}
            </p>
          </div>
          <TicketEntry ticket={props.draw.ticket}></TicketEntry>
          {props.draw.entries >= 0 && (<div className="flex flex-row items-center justify-between w-full ~mt-1/2">
            <p className="font-archb ~text-xxs/xs bg-clip-text text-transparent bg-gradient-to-br from-mango to-crail">
              TOTAL ENTRIES
            </p>
            <p className="font-archb ~text-xs/sm text-silver">
              {props.draw.entries.toString()}
            </p>
          </div>)}
          <div className="flex flex-row items-center justify-between w-full ~mt-1/2">
            <p className="font-archb ~text-xxs/xs bg-clip-text text-transparent bg-gradient-to-br from-persian-pink to-cadillac">
              TOTAL WINNERS
            </p>
            <p className="font-archb ~text-xs/sm text-silver">
              {props.draw.totalWinners.toString()}
            </p>
          </div>
          {props.draw.charity != undefined && (
            <div className="flex flex-row items-center justify-between w-full">
              <p className="font-archb ~text-xxs/xs bg-clip-text text-transparent bg-gradient-to-br from-bermuda to-ming">
                RAISED
              </p>
              <div className="flex flex-row items-center">
                <p className="font-archb ~text-xs/sm bg-clip-text text-transparent bg-gradient-to-b from-white to-emperor">
                  {props.draw.raised.pretty}
                </p>
                <p className="font-archb ~text-xs/sm bg-clip-text text-transparent bg-gradient-to-b from-bermuda to-ming">
                  &nbsp;{CURRENCY}
                </p>
              </div>
            </div>
          )}
          <span
            className={
              "self-start font-archb ~text-xxs/xs ~leading-4/5 " +
              (props.draw.rollover
                ? "text-gray"
                : "bg-gradient-to-b from-mango to-crail bg-clip-text text-transparent")
            }
          >
            {props.draw.rollover ? "ROLLOVER" : "SOMEONE HIT THE JACKPOT!"}
          </span>
          {played && (
            <hr className="~my-3/4.5 w-full bg-gray h-0.5 self-start"></hr>
          )}
          {played && props.draw.userPrize != undefined && (
            <div className="flex flex-row justify-between w-full items-center">
              <p className="font-archb ~text-xxs/xs bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
                YOUR PRIZES
              </p>
              <div className="flex flex-row ml-2">
                <p className="font-archb ~text-xs/sm bg-clip-text text-transparent bg-gradient-to-b from-white to-emperor">
                  {props.draw.userPrize.pretty}
                </p>
                <p className="font-archb ~text-xs/sm bg-clip-text text-transparent bg-gradient-to-b from-mango to-crail">
                  &nbsp;{CURRENCY}
                </p>
              </div>
            </div>
          )}
          {props.address != undefined &&
            props.draw.charity?.address == props.address && (
              <div className="flex flex-row justify-between w-full items-center">
                <p className="font-archb ~text-xxs/xs bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor">
                  RAISED FOR YOUR CAUSE
                </p>
                <div className="flex flex-row ml-2">
                  <p className="font-archb ~text-xs/sm bg-clip-text text-transparent bg-gradient-to-b from-white to-emperor">
                    {props.draw.raised.pretty}
                  </p>
                  <p className="font-archb ~text-xs/sm bg-clip-text text-transparent bg-gradient-to-b from-bermuda to-ming">
                    &nbsp;{CURRENCY}
                  </p>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
